import React from "react";
import { Button } from "./Button.jsx";

const TYPE_STYLES = {
  info: {
    bar: "bg-info",
    icon: "info",
    accent: "text-info",
  },
  success: {
    bar: "bg-success",
    icon: "check_circle",
    accent: "text-success",
  },
  warning: {
    bar: "bg-warning",
    icon: "warning",
    accent: "text-warning",
  },
  error: {
    bar: "bg-danger",
    icon: "error",
    accent: "text-danger",
  },
};

/**
 * ToastContainer — pinta los toasts que produce useToast().
 *
 * NO se monta a mano: el único que lo renderiza es `<ToastProvider>`
 * (`hooks/useToast.jsx`), montado en la raíz de `App.jsx`. Cuando cada pantalla
 * montaba el suyo pasaban las dos cosas: la que se olvidaba se quedaba sin
 * avisos, y dos pantallas anidadas que se acordaban apilaban dos cajas `fixed`
 * en la misma esquina. `toastProvider.guard.test.jsx` falla si vuelve a
 * aparecer un segundo renderizador.
 *
 * Usa `fixed` para flotar sobre el contenido con scroll. Al colgar del
 * provider —fuera de cualquier `SlidePanel`— el `fixed` es relativo al
 * viewport: dentro del panel, la transformada de framer-motion lo convertía en
 * un bloque contenedor y el toast se posicionaba respecto del panel.
 *
 * Props:
 *   toasts  { id, msg, type, icon?, action? }[]  — del estado del provider
 */
export default function ToastContainer({ toasts }) {
  const items = toasts ?? [];

  return (
    // El contenedor se monta SIEMPRE, aunque esté vacío. `role="status"` +
    // `aria-live` anuncian los CAMBIOS de una región que ya existe: una región
    // que aparece con el texto adentro no se anuncia, que es justo el caso del
    // primer toast — y el aviso de permiso denegado suele ser el único en
    // pantalla. Sin esto, el usuario de lector de pantalla no se entera de que
    // hay un botón "Abrir ajustes", que es la única salida del bloqueo.
    <div
      role="status"
      aria-live="polite"
      className="fixed top-16 right-4 z-[700] flex flex-col items-end gap-2 pointer-events-none"
    >
      {items.map((t) => {
        const s = TYPE_STYLES[t.type] ?? TYPE_STYLES.info;
        return (
          <div
            key={t.id}
            className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-surface-1 sheen-2 shadow-panel max-w-sm pointer-events-auto"
            style={{ animation: "toastIn 0.25s ease-out" }}
          >
            {/* Left accent bar */}
            <div
              className={`w-1 self-stretch rounded-full shrink-0 ${s.bar}`}
            />
            <span
              className={`material-symbols-outlined shrink-0 ${s.accent}`}
              style={{ fontSize: "18px" }}
            >
              {t.icon ?? s.icon}
            </span>
            <p className="text-sm leading-snug text-text-primary">{t.msg}</p>
            {/*
              Salida a mano para el aviso que, por sí solo, es un callejón sin
              salida: un permiso que Android ya no vuelve a pedir no se destraba
              desde la app, y "andá a ajustes" deja al usuario buscando entre
              menús.

              `type="button"` explícito: `Button` renderiza un `<button>` sin
              `type`, o sea `submit`, y este contenedor se monta en pantallas
              con formularios.

              `border-border-strong` en vez del borde propio del `secondary`,
              que es `border-border` y sobre `surface-1` da 1.49:1 — el botón se
              lee como texto. El token fuerte es el que cumple el 3:1 de WCAG
              1.4.11 para identificar un control, y acá hace falta de verdad: es
              un botón chico, encima de un mapa, en un teléfono, con ocho
              segundos de vida.
            */}
            {t.action && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={t.action.onClick}
                className="shrink-0 border-border-strong"
              >
                {t.action.label}
              </Button>
            )}
          </div>
        );
      })}
      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateY(12px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
      `}</style>
    </div>
  );
}
